import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../firebase/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import WIcon from '../../components/shared/WIcon';
import Modal from '../../components/shared/Modal';

const formatDate = (value) => {
  if (!value) return '—';
  const d = typeof value === 'string'
    ? new Date(value)
    : (value.toDate ? value.toDate() : new Date(value));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const normalizePhone = (raw) => {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 9) return `+995${digits}`;
  if (digits.length === 12 && digits.startsWith('995')) return `+${digits}`;
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  return `+995${digits}`;
};

const StaffModal = ({ vendorId, addedBy, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '' });

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (error) setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) return setError('Staff name is required.');
    if (!form.phone.trim()) return setError('Phone number is required.');

    const normalized = normalizePhone(form.phone);
    if (normalized.length < 10) {
      return setError('Please enter a valid phone number.');
    }

    setSubmitting(true);
    try {
      const dupSnap = await getDocs(
        query(collection(db, 'vendorStaff'), where('phone', '==', normalized))
      );
      if (!dupSnap.empty) {
        setSubmitting(false);
        return setError('A staff member with this phone number already exists.');
      }

      await addDoc(collection(db, 'vendorStaff'), {
        name: form.name.trim(),
        phone: normalized,
        vendorId,
        addedBy,
        isActive: true,
        createdAt: new Date(),
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Error adding vendor staff:', err);
      setError(err.message || 'Failed to add staff member.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Add staff" onClose={onClose} width={460}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="loysta-alert error">{error}</div>}

        <div className="form-group">
          <label>Full name *</label>
          <input
            className="form-control"
            value={form.name}
            onChange={setField('name')}
            placeholder="e.g. Mariam Beridze"
            required
          />
        </div>

        <div className="form-group">
          <label>Mobile number *</label>
          <input
            className="form-control"
            value={form.phone}
            onChange={setField('phone')}
            placeholder="5XXXXXXXX (9 digits, +995 added automatically)"
            required
          />
          <span className="form-hint">
            Staff member signs into the Loysta Vendor app with this number and OTP <strong>123456</strong>.
          </span>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" className="cancel-btn" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            {submitting ? 'Adding…' : 'Add staff'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const VendorStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchStaff = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const snap = await getDocs(
        query(collection(db, 'vendorStaff'), where('vendorId', '==', auth.currentUser.uid))
      );
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dbb - da;
      });
      setStaff(list);
    } catch (err) {
      console.error('Error fetching vendor staff:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'vendorStaff', confirmDelete.id));
      setStaff((prev) => prev.filter((s) => s.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting vendor staff:', err);
    }
  };

  const filtered = staff.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.phone || '').includes(q);
  });

  return (
    <>
      {showModal && auth.currentUser && (
        <StaffModal
          vendorId={auth.currentUser.uid}
          addedBy={auth.currentUser.email || ''}
          onClose={() => setShowModal(false)}
          onSaved={fetchStaff}
        />
      )}

      {confirmDelete && (
        <Modal title="Remove staff" onClose={() => setConfirmDelete(null)} width={420}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
              Remove <strong>{confirmDelete.name}</strong>? They will no longer be able to sign into the Loysta Vendor app.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="cancel-btn" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={handleDelete}>Remove</button>
            </div>
          </div>
        </Modal>
      )}

      <div className="loysta-card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            {filtered.length} {filtered.length === 1 ? 'staff' : 'staff'}
          </span>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#00bf63', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
          >
            <WIcon name="plus" size={14} color="white" />
            Add staff
          </button>
        </div>

        <table className="loysta-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Added</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading staff…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {staff.length === 0
                  ? 'No staff yet. Click "Add staff" to register your first team member.'
                  : 'No staff match your search.'}
              </td></tr>
            ) : filtered.map((s) => (
              <tr key={s.id}>
                <td style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{s.name}</td>
                <td style={{ color: '#64748b', fontSize: 13, fontFamily: 'monospace' }}>{s.phone}</td>
                <td style={{ color: '#94a3b8', fontSize: 12 }}>{formatDate(s.createdAt)}</td>
                <td>
                  <button
                    className="loysta-icon-btn danger"
                    onClick={() => setConfirmDelete(s)}
                    title="Remove"
                  >
                    <WIcon name="trash" size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default VendorStaff;
